/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import * as Sentry from "@sentry/react"
import {
  buildSegmentOptionsWithPrivacy,
  getEffectivePrivacyChoicesFromBrowser,
  isTrackingPurposeAllowed,
  type SegmentOptionsWithPurpose,
  type TrackingPurpose,
} from "@coast/utils"

declare const window: any

const GOOGLE_TAG_ID = "AW-865171774"
const GOOGLE_EVENT_TO_ID_MAPPING: { [event: string]: string } = {
  "Paid for Course": "jT9_CO34hLQBEL7yxZwD",
  "Lessons Purchased": "mOTbCOCN_HoQvvLFnAM",
  "Package Purchased": "Po__CNSmluwZEL7yxZwD",
}
const BING_PRODUCT_LABELS = Object.keys(GOOGLE_EVENT_TO_ID_MAPPING)

const IS_PRODUCTION = process.env.REACT_APP_ENV === "production"

const getPrivacyAwareSegmentOptions = (
  options: SegmentOptionsWithPurpose | undefined,
  defaultPurpose: TrackingPurpose,
): {
  allowed: boolean
  segmentOptions: Record<string, unknown>
  marketingAllowed: boolean
} => {
  const effectiveChoices = getEffectivePrivacyChoicesFromBrowser()
  const optionsWithPurpose: SegmentOptionsWithPurpose = {
    ...(options ?? {}),
    purpose: options?.purpose ?? defaultPurpose,
  }

  const { purpose, segmentOptions } = buildSegmentOptionsWithPrivacy(
    optionsWithPurpose,
    effectiveChoices,
  )

  if (!isTrackingPurposeAllowed(purpose, effectiveChoices)) {
    return {
      allowed: false,
      segmentOptions,
      marketingAllowed: false,
    }
  }

  return {
    allowed: true,
    segmentOptions,
    marketingAllowed: effectiveChoices.marketingAllowed,
  }
}

function trackMNTNConversion(orderId: string, amount: number) {
  const advertiserId = "43316"

  const encode = (str: string) => encodeURIComponent(str).substring(0, 512)

  const referrer = encode(document.referrer || "")
  const pageLocation = encode(document.location.href)

  const cacheBuster = Math.floor(Math.random() * 1e17)

  const queryParams = new URLSearchParams({
    conv: "1",
    shaid: advertiserId,
    tdr: referrer,
    plh: pageLocation,
    cb: cacheBuster.toString(),
    shoid: orderId,
    shoamt: amount.toString(),
    shocur: "USD",
  })

  const script = document.createElement("script")
  script.type = "text/javascript"
  script.src = `https://dx.mountain.com/spx?${queryParams.toString()}`

  const lastScript = document.getElementsByTagName("script")
  const refScript = lastScript[lastScript.length - 1]
  refScript.parentNode?.insertBefore(script, refScript)
}

export const page = (label: string) => {
  if (IS_PRODUCTION) {
    const { allowed, segmentOptions, marketingAllowed } =
      getPrivacyAwareSegmentOptions(undefined, "analytics")

    if (!allowed) {
      return
    }

    window.analytics.page(label, undefined, undefined, segmentOptions)

    if (marketingAllowed && window.ndp) {
      window.ndp("track", "PAGE_VIEW")
    }
  } else {
    console.log("page:", label)
  }
}

export const segmentIdentify = (uid: string, payload: any) => {
  const newPayload = { ...payload }
  if (IS_PRODUCTION) {
    const { allowed, segmentOptions } = getPrivacyAwareSegmentOptions(
      undefined,
      "operational",
    )

    if (!allowed) {
      return
    }

    window.analytics.identify(uid, newPayload, segmentOptions)
  } else {
    console.log("identify:", uid, newPayload)
  }
}

export const identify = (uid: string, payload: any = null) => {
  {
    const scope = Sentry.getCurrentScope()
    scope.setUser({
      email: payload.email,
      id: uid,
      isAdmin: payload.isAdmin ?? false,
      isInstructor: payload.isInstructor ?? false,
    })
    scope.setTags({
      isAdmin: payload.isAdmin ?? false,
      isInstructor: payload.isInstructor ?? false,
    })
  }

  if (IS_PRODUCTION) {
    const marketingAllowed =
      getEffectivePrivacyChoicesFromBrowser().marketingAllowed

    if (marketingAllowed && window.gtag && payload.email) {
      const email = payload.billingEmail
        ? [payload.email, payload.billingEmail]
        : payload.email
      window.gtag("set", "user_data", { email })
    }
  }
}

type SegmentTrackOptions = SegmentOptionsWithPurpose

export const track = (
  label: string,
  payload: any = null,
  options?: SegmentTrackOptions,
) => {
  if (!IS_PRODUCTION) {
    console.log("track:", label, payload, options)
    return
  }

  const { allowed, segmentOptions, marketingAllowed } =
    getPrivacyAwareSegmentOptions(options, "operational")

  if (!allowed) {
    return
  }

  window.analytics.track(label, payload, segmentOptions)

  if (!marketingAllowed) {
    return
  }

  if (window.gtag && GOOGLE_EVENT_TO_ID_MAPPING[label]) {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_TAG_ID}/${GOOGLE_EVENT_TO_ID_MAPPING[label]}`,
      value: payload.value,
      currency: "USD",
      transaction_id: payload.orderId,
    })
  }

  if (BING_PRODUCT_LABELS.includes(label)) {
    window.uetq = window.uetq || []
    const bingRevenue = typeof payload?.value === "number" ? payload.value : 0
    const bingOrderId =
      typeof payload?.orderId === "string" && payload.orderId.trim().length > 0
        ? payload.orderId
        : undefined
    const bingProductLabel =
      typeof payload?.label === "string" && payload.label.trim().length > 0
        ? payload.label
        : label

    window.uetq.push("event", "PRODUCT_PURCHASE", {
      event_category: "Purchase",
      event_action: label,
      event_label: bingProductLabel,
      ecomm_prodid: bingOrderId ?? bingProductLabel,
      ecomm_pagetype: "PURCHASE",
      revenue_value: bingRevenue,
      revenue: bingRevenue,
      conversionValue: bingRevenue,
      conversion_value: bingRevenue,
      currency: "USD",
    })
  }

  if (label === "Order Completed") {
    window.uetq = window.uetq || []
    window.uetq.push("event", "track", {
      event_label: "Order Completed",
      revenue_value: typeof payload?.value === "number" ? payload.value : 0,
      currency: "USD",
    })

    if (window.ndp) {
      window.ndp("track", "CONVERSION")
    }

    trackMNTNConversion(payload.orderId, payload.value)
  }
}
